import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SitesContext, {
  currentSiteIdAtom,
  useOptionalSiteIdParam,
} from "./context/SitesContext";
import { isDev } from "./env";
import { useSearchParams } from "./global_functions/hooks";
import type { CurrentUser } from "./global_functions/backpackSdk/auth/currentUser";
import { outstandingTerms } from "./global_functions/backpackSdk/auth/termsAndConditions";
import { bugsnagGeneralErrorHandler } from "./global_functions/postgrestApi";
import { Account } from "./types";

interface Metadata {
  [key: string]: string | number | boolean | string[] | null;
}

export const identify = (
  currentUser: CurrentUser,
  account: Partial<Account>,
  nominatorName?: string
) => {
  const outstanding = outstandingTerms(currentUser.termsAndConditions);

  if (isDev) {
    // eslint-disable-next-line no-console
    console.info("Pendo identify", {
      visitorId: String(currentUser.id),
      termsSatisfied: outstanding.length === 0,
      outstandingTerms: outstanding.map(({ category }) => category),
      isSelfSignUp: currentUser.isSelfSignUp ?? false,
      nominatorName: nominatorName || null,
    });
  }

  window.pendo?.identify({
    visitor: {
      id: String(currentUser.id),
      name: `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`,
      email: currentUser.email,
      // `role` is the established Pendo visitor property key and holds the job
      // title - not to be confused with the API's own `role` field
      role: currentUser.superAdmin
        ? "admin"
        : currentUser.jobTitle ?? "unassigned",

      accountName: account.name || null,

      nominatorName: nominatorName || null,
      accountType: account.accountType ?? null,
      permissions: currentUser.userPermissions,
      isSelfSignUp: currentUser.isSelfSignUp ?? false,
      termsSatisfied: outstanding.length === 0,
    },
    account: {
      id: String(account.id),

      name: account.name || null,
      accountType: account.accountType ?? null,

      nominatorName: nominatorName || null,
      nominatorId: currentUser.fromNominator ?? null,
    },
  });

  window?.FS?.identify(currentUser.id, {
    email: currentUser.email,
    accountName_string: account.name,
  });
};

const ANONYMOUS_ID = "VISITOR-UNIQUE-ID";
export const initializeAnonymous = () => {
  window.pendo?.initialize({
    visitor: {
      id: ANONYMOUS_ID,
    },
  });
};

interface Event {
  trackType: string;
  metadata?: Metadata;
}

// Prefer `useTrackEvent` for Backpack events so current building data is automatically included
const track = ({ trackType, metadata }: Event) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.info("Track Event", {
      trackType,
      metadata,
    });
    // pendo not initialized in dev mode. return here to prevent creating a bugsnag error
    return;
  }

  try {
    window.pendo?.track(trackType, metadata);
  } catch (e) {
    bugsnagGeneralErrorHandler(e);
  }
};

const telemetryEventQueueAtom = atom<Event[]>([]);
const pushTelemetryEventAtom = atom(null, (get, set, update: Event) => {
  set(telemetryEventQueueAtom, [...get(telemetryEventQueueAtom), update]);
});

// Returns track function that assigns common metadata from context
export const useTrackEvent = () => {
  const siteIdFromParam = useOptionalSiteIdParam();
  // Backup siteId stored in atom for components nested outside of SiteDashboard
  const siteIdFromAtom = useAtomValue(currentSiteIdAtom);
  const siteId = siteIdFromParam ?? siteIdFromAtom ?? undefined;

  const pushEvent = useSetAtom(pushTelemetryEventAtom);
  return useCallback(
    (trackType: string, metadata?: Metadata) => {
      pushEvent({
        trackType,
        metadata: siteId
          ? {
              // Cast to string since Pendo will not group by number types
              buildingId: String(siteId),
              ...metadata,
            }
          : metadata,
      });
    },
    [pushEvent, siteId]
  );
};

// Only sends one track event when mounted
export const useTrackOnMount = (trackType: string, metadata?: Metadata) => {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent(trackType, metadata);
  }, [trackEvent, trackType, metadata]);
};

const hubspotOpenedClass = "hs-messages-widget-open";
const HubspotChatFeatureTracker = () => {
  const trackEvent = useTrackEvent();

  // Track Hubspot chat feature
  // Hubspot chat is in an iframe but it toggles a classname on the <html> element when opened
  useEffect(() => {
    let lastHubspotOpened = false;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const hubspotOpened =
            document.documentElement.classList.contains(hubspotOpenedClass);
          if (hubspotOpened && !lastHubspotOpened) {
            trackEvent("Chat Feature");
          }
          lastHubspotOpened = hubspotOpened;
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, [trackEvent]);

  return null;
};

const EMAIL_CAMPAIGN_QUERY_PARAM = "email_campaign";
const EmailCampaignTracker = () => {
  const trackEvent = useTrackEvent();

  const { getParsedParam, removeSearchParams } = useSearchParams();
  const { pathname } = useLocation();

  const emailCampaign = getParsedParam(EMAIL_CAMPAIGN_QUERY_PARAM, String);

  // if email campaign is present, track it once then remove the query param
  useEffect(() => {
    if (emailCampaign) {
      trackEvent("Email Campaign", {
        emailCampaign,
        fullPath: window.location.href,
      });
      removeSearchParams([EMAIL_CAMPAIGN_QUERY_PARAM]);
    }
  }, [trackEvent, emailCampaign, removeSearchParams, pathname]);

  return null;
};

export const BackpackTelemetryManager = () => {
  const [eventQueue, setEventQueue] = useAtom(telemetryEventQueueAtom);
  const { currentAccountSites: sites, isLoading: loading } =
    useContext(SitesContext);

  // Queue is used to hold events until sites data has finished loading, then flushes immediately
  // for each event thereafter
  useEffect(() => {
    if (
      !loading &&
      eventQueue.length > 0 &&
      // delay running until pendo is initialized, unless running app in dev mode
      // pendo is not used in dev mode but we still want to log events to the console / clear the event queue
      (typeof window.pendo?.track === "function" || isDev)
    ) {
      eventQueue.forEach((event) => {
        const metadata = { ...event.metadata };
        const siteId = Number(event.metadata?.buildingId);

        // Supplement metadata with site data
        if (siteId) {
          const site = sites.find((s) => s.id === siteId);

          if (site) {
            metadata.buildingName = site.name;
          }
        }

        track({ ...event, metadata });
      });

      setEventQueue([]);
    }
  }, [eventQueue, sites, setEventQueue, loading]);

  return (
    <>
      <HubspotChatFeatureTracker />
      <EmailCampaignTracker />
    </>
  );
};
